import React from "react";
import { ToastContainer, type ToastContentProps } from "react-toastify";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { useResolvedTheme } from "../../features/theme/useResolvedTheme";

/** Toast variants carry their meaning in the icon; the card itself stays neutral. */
const variantIcon = ({ type }: { type?: string }) => {
  switch (type) {
    case "success":
      return <CheckCircle2 size={18} className="shrink-0 text-green-700 dark:text-green-400" />;
    case "error":
      return <AlertTriangle size={18} className="shrink-0 text-red-600 dark:text-red-400" />;
    case "warning":
      return <AlertTriangle size={18} className="shrink-0 text-secondary-40 dark:text-secondary-80" />;
    default:
      return <Info size={18} className="shrink-0 text-primary-40 dark:text-primary-90" />;
  }
};

const CloseButton = ({ closeToast }: Partial<ToastContentProps>) => (
  <button
    type="button"
    onClick={closeToast}
    aria-label="Close"
    className="flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-md text-grey-40 transition-colors hover:bg-grey-95 hover:text-grey-20 dark:text-grey-60 dark:hover:bg-primary-20 dark:hover:text-white"
  >
    <X size={14} />
  </button>
);

/**
 * App-wide toasts, styled as the same bordered card the rest of the UI uses.
 * The theme comes from the resolved app theme (never the DOM), so the very
 * first toast after a load is already correct in dark and auto mode.
 */
const AppToastContainer: React.FC = () => {
  const theme = useResolvedTheme();

  return (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme}
      icon={variantIcon}
      closeButton={CloseButton}
    />
  );
};

export default AppToastContainer;
